import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { TimesheetFlowContext } from "@/features/timesheetFlow/timesheetFlowContextValue";
import { TimesheetFlowHost, orderToPreset } from "@/features/timesheetFlow/TimesheetFlowHost";
import type {
  AuftragsartRoute,
  TimesheetDetailTab,
  TimesheetFlowScreen,
  TimesheetProjectPreset,
} from "@/features/timesheetFlow/timesheetFlowTypes";
import { useInAppWindowFocus } from "@/lib/inAppWindowFocus";
import { MODAL_STACK_GROUP_TIMESHEET, useModalStackControls } from "@/lib/modalStack";
import type { Order } from "@/lib/projects";
import { getProjectCustomFieldSchemas } from "@/lib/projects";
import { getOrderForTimesheet, getTimesheetDetail } from "@/features/timesheetFlow/timesheetFlowApi";
import { getLieferscheinDetail } from "@/lib/lieferscheine";
import {
  createScreenForAuftragsartRoute,
  resolveAuftragsartRoute,
  resolveProjectAuftragsart,
} from "@/features/timesheetFlow/lib/auftragsart";
import { useTimesheetDeepLink } from "@/features/timesheetFlow/useTimesheetDeepLink";

function TimesheetDeepLinkBridge() {
  useTimesheetDeepLink();
  return null;
}

export function TimesheetFlowProvider({ children }: { children: ReactNode }) {
  const { focusSurface } = useInAppWindowFocus();
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState<TimesheetFlowScreen>("projectPicker");
  const [project, setProject] = useState<TimesheetProjectPreset | null>(null);
  const [timesheetId, setTimesheetId] = useState<number | null>(null);
  const [lieferscheinId, setLieferscheinId] = useState<number | null>(null);
  const [detailInitialTab, setDetailInitialTab] = useState<TimesheetDetailTab | null>(null);
  const [auftragsartValue, setAuftragsartValue] = useState<string | null>(null);
  const [, setAuftragsartRoute] = useState<AuftragsartRoute | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const listAtContactPatchRef = useRef<((timesheetId: number, atContact: boolean) => void) | null>(
    null,
  );

  const { bringGroupToFront } = useModalStackControls();

  const focusTimesheet = useCallback(() => {
    focusSurface("timesheet");
    bringGroupToFront(MODAL_STACK_GROUP_TIMESHEET);
  }, [focusSurface, bringGroupToFront]);

  const registerListAtContactPatch = useCallback(
    (patch: (timesheetId: number, atContact: boolean) => void) => {
      listAtContactPatchRef.current = patch;
      return () => {
        if (listAtContactPatchRef.current === patch) {
          listAtContactPatchRef.current = null;
        }
      };
    },
    [],
  );

  const patchListAtContact = useCallback((timesheetId: number, atContact: boolean) => {
    listAtContactPatchRef.current?.(timesheetId, atContact);
  }, []);

  const reset = useCallback(() => {
    setScreen("projectPicker");
    setProject(null);
    setTimesheetId(null);
    setLieferscheinId(null);
    setDetailInitialTab(null);
    setAuftragsartValue(null);
    setAuftragsartRoute(null);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  const requestRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const openCreate = useCallback(() => {
    reset();
    focusTimesheet();
    setOpen(true);
    setScreen("projectPicker");
  }, [reset, focusTimesheet]);

  const openCreateForProject = useCallback(
    (p: TimesheetProjectPreset | Order) => {
      const preset =
        "auftragsnummer" in p && "hero_custom_fields" in p
          ? orderToPreset(p as Order)
          : (p as TimesheetProjectPreset);
      reset();
      focusTimesheet();
      setProject(preset);
      setOpen(true);
      void (async () => {
        const schemas = await getProjectCustomFieldSchemas().catch(() => []);
        const art = resolveProjectAuftragsart(preset, schemas);
        const route = resolveAuftragsartRoute(art);
        setAuftragsartValue(art);
        setAuftragsartRoute(route);
        // Festpreis: nur Fertigstellungsmeldung. Lieferschein schreibt die GF von der Home-Seite.
        setScreen(createScreenForAuftragsartRoute(route) ?? "timesheetEditor");
      })();
    },
    [reset, focusTimesheet],
  );

  const openLieferscheinForProject = useCallback(
    (p: TimesheetProjectPreset | Order) => {
      const preset =
        "auftragsnummer" in p && "hero_custom_fields" in p
          ? orderToPreset(p as Order)
          : (p as TimesheetProjectPreset);
      reset();
      focusTimesheet();
      setProject(preset);
      setOpen(true);
      void (async () => {
        const schemas = await getProjectCustomFieldSchemas().catch(() => []);
        const art = resolveProjectAuftragsart(preset, schemas);
        setAuftragsartValue(art);
        setAuftragsartRoute(resolveAuftragsartRoute(art));
        setScreen("lieferscheinEditor");
      })();
    },
    [reset, focusTimesheet],
  );

  const openMuendlichBeauftragt = useCallback(
    (p: TimesheetProjectPreset | Order) => {
      const preset =
        "auftragsnummer" in p && "hero_custom_fields" in p
          ? orderToPreset(p as Order)
          : (p as TimesheetProjectPreset);
      reset();
      focusTimesheet();
      setProject(preset);
      setOpen(true);
      setScreen("muendlichBeauftragt");
    },
    [reset, focusTimesheet],
  );

  const loadProjectContext = useCallback(async (auftragId: number) => {
    const order = await getOrderForTimesheet(auftragId);
    const preset = orderToPreset(order as Order);
    setProject(preset);
    const schemas = await getProjectCustomFieldSchemas().catch(() => []);
    const art = resolveProjectAuftragsart(preset, schemas);
    setAuftragsartValue(art);
    setAuftragsartRoute(resolveAuftragsartRoute(art));
    return preset;
  }, []);

  const openEdit = useCallback(
    (id: number) => {
      reset();
      focusTimesheet();
      setTimesheetId(id);
      setOpen(true);
      setScreen("timesheetEditor");
      void (async () => {
        const bundle = await getTimesheetDetail(id);
        const auftragId = bundle.timesheet.auftrag_id;
        if (!auftragId) return;
        await loadProjectContext(auftragId);
      })();
    },
    [reset, focusTimesheet, loadProjectContext],
  );

  const showLieferscheinDetail = useCallback((dokumentId: number) => {
    setLieferscheinId(dokumentId);
    setScreen("lieferscheinDetail");
    setOpen(true);
  }, []);

  const openLieferscheinDetail = useCallback(
    (dokumentId: number, auftragId?: number | null) => {
      const start = (id: number) => {
        reset();
        focusTimesheet();
        setLieferscheinId(dokumentId);
        setProject({ id });
        setOpen(true);
        setScreen("lieferscheinDetail");
      };
      if (auftragId) {
        start(auftragId);
        return;
      }
      void getLieferscheinDetail(dokumentId)
        .then((detail) => {
          if (detail.auftrag_id) start(detail.auftrag_id);
        })
        .catch(() => undefined);
    },
    [reset, focusTimesheet],
  );

  const openDetail = useCallback(
    (id: number, options?: { initialTab?: TimesheetDetailTab }) => {
      reset();
      focusTimesheet();
      setTimesheetId(id);
      setDetailInitialTab(options?.initialTab ?? null);
      setOpen(true);
      setScreen("detail");
      void (async () => {
        const bundle = await getTimesheetDetail(id);
        const auftragId = bundle.timesheet.auftrag_id;
        if (!auftragId) return;
        await loadProjectContext(auftragId);
      })();
    },
    [reset, focusTimesheet, loadProjectContext],
  );

  const value = useMemo(
    () => ({
      openCreate,
      openCreateForProject,
      openLieferscheinForProject,
      openLieferscheinDetail,
      openMuendlichBeauftragt,
      openEdit,
      openDetail,
      close,
      requestRefresh,
      registerListAtContactPatch,
      patchListAtContact,
      refreshKey,
      isOpen: open,
    }),
    [
      openCreate,
      openCreateForProject,
      openLieferscheinForProject,
      openLieferscheinDetail,
      openMuendlichBeauftragt,
      openEdit,
      openDetail,
      close,
      requestRefresh,
      registerListAtContactPatch,
      patchListAtContact,
      refreshKey,
      open,
    ],
  );

  return (
    <TimesheetFlowContext.Provider value={value}>
      {children}
      <TimesheetDeepLinkBridge />
      <TimesheetFlowHost
        open={open}
        screen={screen}
        project={project}
        timesheetId={timesheetId}
        lieferscheinId={lieferscheinId}
        detailInitialTab={detailInitialTab}
        auftragsartValue={auftragsartValue}
        onScreenChange={setScreen}
        onProjectChange={setProject}
        onTimesheetIdChange={setTimesheetId}
        onAuftragsartChange={(v, r) => {
          setAuftragsartValue(v);
          setAuftragsartRoute(r);
        }}
        onClose={close}
        onSaved={requestRefresh}
        onShowLieferscheinDetail={showLieferscheinDetail}
        onPatchListAtContact={patchListAtContact}
      />
    </TimesheetFlowContext.Provider>
  );
}
